/** One clip's lanes (GET /api/clips/{id}/lanes.json) projected for the Choose page: the clip as a Timeline on the
 * clip clock (so the Watch projections in `timeline/project.ts` and lane rows work unchanged with view {0, dur}), the
 * Vision lane (colour strip, CCT line, subject band), and the canvas overlay geometry. Pure. */
import type { Schemas } from "../../api/client";
import { apiFileUrl } from "../../lib/identity";
import { BEAT_LAYERS, pct, type View } from "../timeline/project";
import type { Timeline } from "../types";

export type ClipLanes = Schemas["ClipLanes"];
export type VisionSample = Schemas["VisionSample"];
export type CanvasOut = Schemas["CanvasOut"];
export type PaletteOut = Schemas["PaletteOut"];
export type LaneTry = Schemas["LaneTry"];

export const CANVAS_W = 1080;
export const CANVAS_H = 1920;
/** CCT axis of the Vision lane's line (K): warm at the bottom, cool at the top. */
export const CCT_MIN = 2000;
export const CCT_MAX = 10000;

export const lanesUrl = (clipId: number, ext: "json" | "csv" | "md", download = false) =>
  apiFileUrl(`/api/clips/${clipId}/lanes.${ext}`, { download: download ? 1 : undefined });

export function clipView(l: Pick<ClipLanes, "dur_ms">): View {
  return { v0: 0, v1: Math.max(1, l.dur_ms) };
}

/** The clip's lanes as a Timeline whose `*_mv_ms` fields carry clip-clock times (t_clip_ms). */
export function clipTimeline(l: ClipLanes): Timeline {
  const layers: Record<string, number[]> = {};
  const pos: Record<string, (string | null)[]> = {};
  for (const name of BEAT_LAYERS) {
    layers[name] = [];
    pos[name] = [];
  }
  for (const b of l.beats) {
    (layers[b.layer] ??= []).push(b.t_clip_ms);
    (pos[b.layer] ??= []).push(b.pos ?? null);
  }
  return {
    video_id: l.video_id ?? 0, song_id: l.song_id ?? null, fps: l.fps ?? null, duration_ms: l.dur_ms,
    segments: [], cutscenes: [], clips: [], suggestions: [], steps: [], stems: null,
    sections: l.sections.map((s) => ({ label: s.label, t0_mv_ms: s.t0_clip_ms, t1_mv_ms: s.t1_clip_ms,
      performer: s.performer ?? null, cluster: s.cluster ?? null })),
    lines: l.lines.map((x, i) => ({ idx: x.idx ?? i, t0_mv_ms: x.t0_clip_ms, t1_mv_ms: x.t1_clip_ms,
      text_raw: x.text_raw, text_masked: x.text_masked, section: x.section ?? null, performer: x.performer ?? null })),
    words: l.words.map((w) => ({ t0_mv_ms: w.t0_clip_ms, t1_mv_ms: w.t1_clip_ms, w_raw: w.w_raw, w_masked: w.w_masked,
      line_idx: w.line_idx ?? -1, flag: w.flag ?? "aligned" })),
    beat_layers_mv_ms: layers, beat_positions: pos, beats_mv_ms: layers.beats ?? [], downbeats_mv_ms: layers.bars ?? [],
    beat_source: l.beat_source ?? null, bpm: l.bpm ?? null, meter: l.meter ?? null, beats_per_bar: l.beats_per_bar ?? null,
    lyrics_clock: "mv", heat_bin_ms: l.heat_bin_ms ?? null,
    heat: l.heat.map((h) => ({ t_mv_ms: h.t_clip_ms, w_play: h.w_play, w_count: h.w_count })),
    hot_windows: l.hot_windows.map((h) => ({ t0_mv_ms: h.t0_clip_ms, t1_mv_ms: h.t1_clip_ms, score: h.score })),
    scenes: l.scenes.map((s) => ({ t_mv_ms: s.t_clip_ms, kind: s.kind, score: s.score, shot_len_ms: s.shot_len_ms ?? null,
      dur_ms: s.dur_ms })),
    scene_stats: l.scene_stats ?? null,
    vision: [], vision_stats: null,  // the Vision lane reads the clip's own samples (below), not the timeline
  };
}

/* ---------------------------------------------------------------- Vision lane */

export type VisionCell = { key: string; t: number; left: number; width: number; colour: string; cctY: number | null;
  subject: number; persons: number; mono: boolean; sample: VisionSample };

/** One cell per sample, from its time to the next sample's (the last one gets the median spacing), clipped to the
 * view. `cctY` = 0 (bottom, warm CCT_MIN) … 1 (top, cool CCT_MAX); `subject` = the person mask share (0..1). */
export function projectVision(samples: readonly VisionSample[], view: View): VisionCell[] {
  const gaps = samples.slice(1).map((s, i) => s.t_clip_ms - (samples[i] as VisionSample).t_clip_ms).sort((a, b) => a - b);
  const step = gaps.length ? (gaps[Math.floor(gaps.length / 2)] as number) : 200;
  const out: VisionCell[] = [];
  samples.forEach((s, i) => {
    const t0 = s.t_clip_ms;
    const t1 = i + 1 < samples.length ? (samples[i + 1] as VisionSample).t_clip_ms : t0 + step;
    if (t1 <= view.v0 || t0 >= view.v1) return;
    const a = Math.max(t0, view.v0);
    const b = Math.min(t1, view.v1);
    out.push({ key: `v${t0}`, t: t0, left: pct(a, view), width: Math.max(0.1, pct(b, view) - pct(a, view)),
      colour: s.mean_hex ?? "transparent", cctY: s.cct_k != null ? cctFraction(s.cct_k) : null,
      subject: s.persons ? Math.max(0.05, Math.min(1, s.subject_area)) : 0, persons: s.persons, mono: s.mono === true,
      sample: s });
  });
  return out;
}

export function cctFraction(k: number): number {
  return Math.min(1, Math.max(0, (k - CCT_MIN) / (CCT_MAX - CCT_MIN)));
}

/** SVG polyline points (viewBox 0 0 100 100) of the CCT line through the cells' centres. */
export function cctPoints(cells: readonly VisionCell[]): string {
  return cells.filter((c) => c.cctY != null)
    .map((c) => `${(c.left + c.width / 2).toFixed(2)},${(100 - (c.cctY as number) * 100).toFixed(2)}`).join(" ");
}

/** The sample nearest to clip time t (null without samples). */
export function nearestSample(samples: readonly VisionSample[], t: number): VisionSample | null {
  let best: VisionSample | null = null;
  for (const s of samples) if (!best || Math.abs(s.t_clip_ms - t) < Math.abs(best.t_clip_ms - t)) best = s;
  return best;
}

/* ---------------------------------------------------------------- canvas overlay */

export type Rect = { x0: number; y0: number; x1: number; y1: number };

/** The part of the 16:9 source the 9:16 canvas shows (fractions of the source frame): the plate is `plate_h_px` high,
 * so min(1080, h·16/9) of its width is visible, panned by crop_x (0 = left edge … 1 = right edge). Full height. */
export function visibleWindow(c: Pick<CanvasOut, "plate_h_px" | "crop_x">): Rect {
  const plateW = (c.plate_h_px * 16) / 9;
  const w = Math.min(1, CANVAS_W / plateW);
  const x0 = Math.min(1, Math.max(0, c.crop_x)) * (1 - w);
  return { x0, y0: 0, x1: x0 + w, y1: 1 };
}

/** A normalised source box as CSS percentages over the preview (the preview shows the whole 16:9 source). */
export function boxStyle(b: readonly number[]): { left: string; top: string; width: string; height: string } | null {
  if (b.length < 4) return null;
  const [x0, y0, x1, y1] = b.map((v) => Math.min(1, Math.max(0, v))) as [number, number, number, number];
  if (x1 <= x0 || y1 <= y0) return null;
  return { left: `${x0 * 100}%`, top: `${y0 * 100}%`, width: `${(x1 - x0) * 100}%`, height: `${(y1 - y0) * 100}%` };
}

export function canvasText(c: CanvasOut): string {
  const pctH = c.plate_pct ?? (c.plate_h_px / CANVAS_H) * 100;
  const vis = typeof c.metrics.visibility === "number" ? ` · visibility ${(c.metrics.visibility * 100).toFixed(0)}%` : "";
  return `plate ${pctH.toFixed(1)}% (${c.plate_h_px} px) · crop_x ${c.crop_x.toFixed(3)} · plate_cy ${c.plate_cy.toFixed(3)}${vis}`;
}

/** metrics.candidates as table rows when the solver reported them (any keys; numbers rounded). */
export function candidates(c: CanvasOut): { cols: string[]; rows: Record<string, unknown>[] } {
  const raw = c.metrics.candidates;
  if (!Array.isArray(raw)) return { cols: [], rows: [] };
  const rows = raw.filter((r): r is Record<string, unknown> => !!r && typeof r === "object" && !Array.isArray(r));
  const cols = [...new Set(rows.flatMap((r) => Object.keys(r)))].slice(0, 8);
  return { cols, rows };
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

/** A try's `palette` (a loose dict in TryOut) as a PaletteOut; null when it is not one. */
export function asPalette(v: unknown): PaletteOut | null {
  if (!isObj(v) || !Array.isArray(v.colours)) return null;
  return { colours: v.colours.filter((c): c is string => typeof c === "string"), stroke: v.stroke === true,
    mix_id: typeof v.mix_id === "string" ? v.mix_id : "", version: typeof v.version === "string" ? v.version : "",
    reason: typeof v.reason === "string" ? v.reason : "", scores: isObj(v.scores) ? v.scores : {} };
}

/** A try's `canvas` (a loose dict in TryOut) as a CanvasOut; null when it has no plate height. */
export function asCanvas(v: unknown): CanvasOut | null {
  if (!isObj(v) || typeof v.plate_h_px !== "number") return null;
  const num = (x: unknown, d: number) => (typeof x === "number" ? x : d);
  return { plate_h_px: v.plate_h_px, plate_pct: typeof v.plate_pct === "number" ? v.plate_pct : null,
    crop_x: num(v.crop_x, 0.5), plate_cy: num(v.plate_cy, 0.5), version: typeof v.version === "string" ? v.version : "",
    source: typeof v.source === "string" ? v.source : "", reason: typeof v.reason === "string" ? v.reason : "",
    metrics: isObj(v.metrics) ? v.metrics : {} };
}

export function cell(v: unknown): string {
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(3);
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (v == null) return "–";
  return typeof v === "string" ? v : JSON.stringify(v);
}
